// src/modules/notifications/service/NotificationService.ts

// SERVICE MOCKADO
// A assinatura imita chamadas de API (async, filtros, paginação).
// Quando a API real estiver disponível, basta trocar os métodos
// para usar httpInstance.get/post/etc, reaproveitando a interface.

import { inject, injectable } from "inversify";
import * as IHttpService from "@modules/infra/http/models/IHttpService";
import { Types } from "@ioc/types";

import {
  INotification,
  INotificationCreateDTO,
  INotificationFilter,
  INotificationRead,
  INotificationService,
  IUserContextForNotification,
  IPaginatedNotifications,
  NotificationStatus,
  ACTIVE,
  DELETED,
  SCHEDULED,
  IUserNotification,
} from "../model/INotifications";
import { buildSeedNotifications } from "../mock/notification.mock";
import { generateMockReads } from "../mock/notifications.helperMock";

const STORAGE_KEY = "mock_notifications";
const READS_KEY_PREFIX = "mock_notifications_reads_";
const READS_SEEDED_KEY = "mock_notifications_reads_seeded";
const NOTIFICATIONS_EMPTY_KEY = "mock_notifications_empty";

const parseDate = (value?: string) => (value ? new Date(value) : undefined);

// Regra de status, simulando backend
const resolveStatus = (notification: INotification, now = new Date()) => {
  if (notification.status === DELETED) return DELETED as NotificationStatus;

  const start = parseDate(notification.startAt)!;
  const end = parseDate(notification.endAt)!;

  if (now < start) return "SCHEDULED" as NotificationStatus;
  if (now > end) return "EXPIRED" as NotificationStatus;
  return ACTIVE as NotificationStatus;
};

@injectable()
export class NotificationService implements INotificationService {
  @inject(Types.IHttpService)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private httpInstance!: IHttpService.IHttpService;

  private seedInitialReads(notifications: INotification[]) {
    if (typeof window === "undefined") return;

    const alreadySeeded = window.localStorage.getItem(READS_SEEDED_KEY);
    if (alreadySeeded) return;

    // 1) filtra apenas as notificações que estão ATIVAS
    const active = notifications.filter((n) => resolveStatus(n) === ACTIVE);

    // 2) pega os 3 primeiros ativos
    const seededActive = active.slice(0, 3);

    // 3) para cada uma delas, gera uma lista de "lidos" e salva
    seededActive.forEach((n) => {
      const reads = generateMockReads();
      this.saveReads(n.id, reads);
    });

    // 4) marca flag para não reseedar toda hora
    window.localStorage.setItem(READS_SEEDED_KEY, "1");
  }

  private loadNotifications(): INotification[] {
    // SSR / testes: sempre gera um seed novo
    if (typeof window === "undefined") return buildSeedNotifications();

    const isEmpty =
      window.localStorage.getItem(NOTIFICATIONS_EMPTY_KEY) === "1";
    if (isEmpty) {
      return [];
    }

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // primeira carga OU pós-reset → gera um novo conjunto
      const initial = buildSeedNotifications();

      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));

      this.seedInitialReads(initial);

      return initial;
    }

    try {
      const parsed: INotification[] = JSON.parse(raw);

      this.seedInitialReads(parsed);

      return parsed;
    } catch {
      // fallback em caso de JSON corrompido → também gera novo seed
      const initial = buildSeedNotifications();
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
      this.seedInitialReads(initial);
      return initial;
    }
  }

  private saveNotifications(notifications: INotification[]) {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  }

  private loadReads(id: number): INotificationRead[] {
    if (typeof window === "undefined") return [];
    const raw = window.localStorage.getItem(`${READS_KEY_PREFIX}${id}`);
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }

  private saveReads(id: number, reads: INotificationRead[]) {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      `${READS_KEY_PREFIX}${id}`,
      JSON.stringify(reads)
    );
  }

  private applyFilters(
    items: INotification[],
    filter: INotificationFilter
  ): INotification[] {
    const now = new Date();

    return items
      .map((n) => ({ ...n, status: resolveStatus(n, now) }))
      .filter((n) => {
        if (typeof filter.id === "number" && n.id !== filter.id) return false;
        if (
          filter.title &&
          !n.title.toLowerCase().includes(filter.title.toLowerCase())
        )
          return false;

        if (filter.periodStart) {
          const startPeriod = parseDate(filter.periodStart)!;
          if (parseDate(n.startAt)! < startPeriod) return false;
        }
        if (filter.periodEnd) {
          const endPeriod = parseDate(filter.periodEnd)!;
          if (parseDate(n.endAt)! > endPeriod) return false;
        }

        if (filter.recipient) {
          const term = filter.recipient.toLowerCase();
          const allTargets = [
            ...n.targets.centers,
            ...n.targets.segments,
            ...n.targets.profiles,
          ]
            .join(",")
            .toLowerCase();

          if (!allTargets.includes(term)) return false;
        }

        if (filter.status && n.status !== filter.status) return false;

        return true;
      });
  }

  private paginate(
    items: INotification[],
    page = 1,
    pageSize = 10
  ): IPaginatedNotifications {
    const startIndex = (page - 1) * pageSize;
    const slice = items.slice(startIndex, startIndex + pageSize);

    return {
      items: slice,
      page,
      pageSize,
      total: items.length,
    };
  }

  async list(filter: INotificationFilter): Promise<IPaginatedNotifications> {
    const data = this.loadNotifications();
    const filtered = this.applyFilters(data, filter);

    return this.paginate(filtered, filter.page || 1, filter.pageSize || 10);
  }

  async getById(id: number): Promise<INotification> {
    const data = this.loadNotifications();
    const found = data.find((x) => x.id === id);

    if (!found) {
      throw new Error("Notificação não encontrada");
    }

    return {
      ...found,
      status: resolveStatus(found),
    };
  }

  async create(
    payload: INotificationCreateDTO,
    createdBy: string
  ): Promise<void> {
    const notifications = this.loadNotifications();

    const nextId =
      notifications.reduce((acc, curr) => Math.max(acc, curr.id), 0) + 1;

    const nowIso = new Date().toISOString();

    const newNotification: INotification = {
      id: nextId,
      title: payload.title,
      message: payload.message,
      targets: payload.targets,
      startAt: payload.startAt,
      endAt: payload.endAt,
      imageUrl: payload.imageUrl,
      createdBy,
      createdAt: nowIso,
      status: SCHEDULED,
    };

    notifications.push(newNotification);
    this.saveNotifications(notifications);
  }

  async update(id: number, payload: INotificationCreateDTO): Promise<void> {
    const notifications = this.loadNotifications();
    const idx = notifications.findIndex((x) => x.id === id);

    if (idx === -1) {
      throw new Error("Notificação não encontrada");
    }

    const existing = notifications[idx];

    notifications[idx] = {
      ...existing,
      title: payload.title,
      message: payload.message,
      targets: payload.targets,
      startAt: payload.startAt,
      endAt: payload.endAt,
      imageUrl: payload.imageUrl,
      // status será recalculado por resolveStatus
    };

    this.saveNotifications(notifications);
  }

  async softDelete(id: number, deletedBy: string): Promise<void> {
    const notifications = this.loadNotifications();
    const idx = notifications.findIndex((x) => x.id === id);

    if (idx === -1) {
      throw new Error("Notificação não encontrada");
    }

    const nowIso = new Date().toISOString();

    notifications[idx] = {
      ...notifications[idx],
      deletedBy,
      deletedAt: nowIso,
      status: DELETED,
    };

    this.saveNotifications(notifications);
  }

  async listReads(id: number): Promise<INotificationRead[]> {
    return this.loadReads(id);
  }

  async listActiveForUser(
    ctx: IUserContextForNotification
  ): Promise<INotification[]> {
    const data = this.loadNotifications();
    const now = new Date();

    return data
      .map((n) => ({ ...n, status: resolveStatus(n, now) }))
      .filter((n) => {
        if (n.status !== ACTIVE) return false;

        const hasCenter =
          ctx.centers.length === 0 ||
          n.targets.centers.some((c) => ctx.centers.includes(c));

        const hasSegment =
          ctx.segments.length === 0 ||
          n.targets.segments.some((s) => ctx.segments.includes(s));

        const hasProfile =
          ctx.profiles.length === 0 ||
          n.targets.profiles.some((p) => ctx.profiles.includes(p));

        return hasCenter && hasSegment && hasProfile;
      });
  }

  async markRead(id: number, ctx: IUserContextForNotification): Promise<void> {
    const reads = this.loadReads(id);

    const exists = reads.some((r) => r.email === ctx.email);
    if (exists) return;

    const read: INotificationRead = {
      name: ctx.name,
      email: ctx.email,
      readAt: new Date().toISOString(),
    };

    this.saveReads(id, [...reads, read]);
  }

  async listForUserWithReadStatus(
    payloadRequest: IUserContextForNotification
  ): Promise<IUserNotification[]> {
    const data = this.loadNotifications();
    const now = new Date();

    return data
      .map((n) => ({ ...n, status: resolveStatus(n, now) }))
      .filter((n) => {
        // aqui você decide a regra de negócio:
        // se quiser só notificações ATIVAS pro usuário:
        if (n.status !== ACTIVE) return false;

        return this.isVisibleToUser(n, payloadRequest);
      })
      .map((n) => {
        const isRead = this.isReadByUser(n.id, payloadRequest.email);

        const userNotification: IUserNotification = {
          ...n,
          isRead,
        };

        return userNotification;
      });
  }

  async resetMocks(): Promise<void> {
    if (typeof window === "undefined") return;

    // Remove lista de notificações
    window.localStorage.removeItem(STORAGE_KEY);

    // Remove flag de seed de leituras
    window.localStorage.removeItem(READS_SEEDED_KEY);

    window.localStorage.removeItem(NOTIFICATIONS_EMPTY_KEY);

    // Remove todas as chaves de leituras: mock_notifications_reads_<id>
    const keys = Object.keys(window.localStorage);
    keys
      .filter((k) => k.startsWith(READS_KEY_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  }
  async clearNotificationsModule(): Promise<void> {
    this.resetMocks();
    window.localStorage.setItem(NOTIFICATIONS_EMPTY_KEY, "1");
  }

  /* HELPERS */
  private isVisibleToUser(
    n: INotification,
    ctx: IUserContextForNotification
  ): boolean {
    const hasCenter =
      ctx.centers.length > 0 &&
      n.targets.centers.some((c) => ctx.centers.includes(c));

    const hasSegment =
      ctx.segments.length > 0 &&
      n.targets.segments.some((s) => ctx.segments.includes(s));

    const hasProfile =
      ctx.profiles.length > 0 &&
      n.targets.profiles.some((p) => ctx.profiles.includes(p));

    return hasCenter || hasSegment || hasProfile;
  }

  private isReadByUser(id: number, email: string): boolean {
    const reads = this.loadReads(id);
    return reads.some((r) => r.email === email);
  }
}
